import { GpmPayConfigError } from '../core/errors.js';

/**
 * Require a flag that has no sensible default.
 *
 * @throws {GpmPayConfigError} `invalid_argument` → exit code 2.
 */
export function requireArg(value: string | undefined, name: string): string {
  if (value === undefined || value === '') {
    throw new GpmPayConfigError(
      `Missing required option --${name}.`,
      'invalid_argument',
    );
  }
  return value;
}

/**
 * Validate a flag against a closed set, case-insensitively.
 *
 * Filters are silent failures by nature — the API happily returns zero rows for
 * `--status FAILDE`, which reads as "nothing to see here" rather than "typo".
 * Every enum-valued flag goes through this so a typo is loud.
 *
 * @throws {GpmPayConfigError} `invalid_argument` → exit code 2.
 */
export function enumArg<T extends string>(
  value: string | undefined,
  name: string,
  allowed: readonly T[],
): T | undefined {
  if (value === undefined || value === '') return undefined;

  const match = allowed.find(
    (candidate) => candidate.toLowerCase() === value.toLowerCase(),
  );
  if (match === undefined) {
    throw new GpmPayConfigError(
      `Invalid --${name} "${value}". Expected one of: ${allowed.join(' | ')}.`,
      'invalid_argument',
    );
  }
  return match;
}

/**
 * Parse a flag that must be a positive integer.
 *
 * `Number('abc')` is `NaN`, and the query builder drops non-finite values — so
 * without this, `--limit abc` silently means "no limit".
 *
 * @throws {GpmPayConfigError} `invalid_argument` → exit code 2.
 */
export function intArg(
  value: string | undefined,
  name: string,
): number | undefined {
  if (value === undefined || value === '') return undefined;

  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    throw new GpmPayConfigError(
      `Invalid --${name} "${value}". Expected a positive integer.`,
      'invalid_argument',
    );
  }
  return parsed;
}
